import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';

interface ChoiceItem {
  product_id: number;
  product_title: string;
  product_format_id: number;
  format_name: string;
  format_price: number;
  quantity: number;
  subtotal: number;
  product_image_url?: string | null;
}

interface ChoiceGroup {
  id: number;
  label: string;
  multiple: boolean;
  items: ChoiceItem[];
}

interface PackChoiceModalProps {
  title: string;
  choiceGroups: ChoiceGroup[];
  fixedItemsTotal: number;
  onConfirm: (selectedItems: ChoiceItem[]) => void;
  onClose: () => void;
  isLoading?: boolean;
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price);
}

export function PackChoiceModal({
  title,
  choiceGroups,
  fixedItemsTotal,
  onConfirm,
  onClose,
  isLoading = false,
}: PackChoiceModalProps) {
  const [selections, setSelections] = useState<Record<number, number[]>>({});
  const sheetRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef<number | null>(null);
  const currentTranslateY = useRef(0);
  const rafPending = useRef(false);

  // Body scroll lock
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const preventScroll = (e: TouchEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.modal-scroll-content')) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventScroll, { passive: false });
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('touchmove', preventScroll);
    };
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleToggleOption = useCallback((group: ChoiceGroup, formatId: number) => {
    setSelections(prev => {
      const current = prev[group.id] ?? [];
      if (group.multiple) {
        const next = current.includes(formatId)
          ? current.filter(id => id !== formatId)
          : [...current, formatId];
        return { ...prev, [group.id]: next };
      } else {
        const next = current.includes(formatId) ? [] : [formatId];
        return { ...prev, [group.id]: next };
      }
    });
  }, []);

  const selectedItems = useMemo(() => {
    const items: ChoiceItem[] = [];
    for (const group of choiceGroups) {
      const selected = selections[group.id] ?? [];
      for (const formatId of selected) {
        const item = group.items.find(i => i.product_format_id === formatId);
        if (item) items.push(item);
      }
    }
    return items;
  }, [choiceGroups, selections]);

  const selectedTotal = useMemo(
    () => selectedItems.reduce((sum, item) => sum + item.subtotal, 0),
    [selectedItems],
  );
  const dynamicPrice = fixedItemsTotal + selectedTotal;
  const showFromPrice = selectedItems.length === 0;

  const handleConfirm = useCallback(() => {
    onConfirm(selectedItems);
  }, [onConfirm, selectedItems]);

  // Drag logic for mobile bottom sheet
  const handleDragStart = useCallback((clientY: number) => {
    dragStartY.current = clientY;
    currentTranslateY.current = 0;
  }, []);

  const handleDragMove = useCallback((clientY: number) => {
    if (dragStartY.current === null || !sheetRef.current) return;
    const dy = clientY - dragStartY.current;
    if (dy > 0) {
      currentTranslateY.current = dy;
      if (!rafPending.current) {
        rafPending.current = true;
        requestAnimationFrame(() => {
          if (sheetRef.current) {
            sheetRef.current.style.transform = `translateY(${currentTranslateY.current}px)`;
          }
          rafPending.current = false;
        });
      }
    }
  }, []);

  const handleDragEnd = useCallback(() => {
    if (dragStartY.current === null || !sheetRef.current) return;
    dragStartY.current = null;
    const sheet = sheetRef.current;

    if (currentTranslateY.current > 80) {
      sheet.style.transition = 'transform 0.2s ease-in';
      sheet.style.transform = 'translateY(100%)';
      const handleTransitionEnd = () => {
        sheet.removeEventListener('transitionend', handleTransitionEnd);
        onClose();
      };
      sheet.addEventListener('transitionend', handleTransitionEnd);
    } else {
      sheet.style.transition = 'transform 0.2s ease-out';
      sheet.style.transform = 'translateY(0)';
      const handleTransitionEnd = () => {
        sheet.removeEventListener('transitionend', handleTransitionEnd);
        sheet.style.transition = '';
      };
      sheet.addEventListener('transitionend', handleTransitionEnd);
    }
  }, [onClose]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) handleDragStart(e.touches[0].clientY);
  }, [handleDragStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) handleDragMove(e.touches[0].clientY);
  }, [handleDragMove]);

  const handleTouchEnd = useCallback(() => {
    handleDragEnd();
  }, [handleDragEnd]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    handleDragStart(e.clientY);
    const onMouseMove = (ev: MouseEvent) => handleDragMove(ev.clientY);
    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      handleDragEnd();
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [handleDragStart, handleDragMove, handleDragEnd]);

  return createPortal(
    <div className="fixed inset-0 z-[10000]" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* White cover for iOS safe area — sits on top of backdrop so dark page content doesn't bleed through */}
      <div className="absolute bottom-0 left-0 right-0 bg-white h-[env(safe-area-inset-bottom)] md:hidden" />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className="absolute bottom-0 left-0 right-0 flex flex-col rounded-t-2xl bg-white shadow-2xl md:static md:inset-auto md:mx-auto md:my-auto md:max-w-lg md:rounded-2xl md:absolute md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:bottom-auto"
        style={{ maxHeight: '85vh', animation: 'pack-choice-slide-up 0.25s ease-out' }}
      >
        {/* Drag handle (mobile only) */}
        <div
          className="shrink-0 touch-none cursor-grab active:cursor-grabbing md:hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          onMouseDown={handleMouseDown}
        >
          <div className="flex justify-center pt-4 pb-2">
            <div className="h-1 w-12 rounded-full bg-gray-300" />
          </div>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <h3 className="m-0 text-lg font-semibold text-gray-800">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-9 w-9 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Cerrar"
          >
            <i className="fa fa-times text-lg" />
          </button>
        </div>

        {/* Choice groups */}
        <div className="modal-scroll-content flex-1 overflow-y-auto px-5 py-4">
          {choiceGroups.map((group) => {
            const groupSelections = selections[group.id] ?? [];
            const hasSelection = groupSelections.length > 0;
            return (
              <div key={group.id} className="mb-6 last:mb-0">
                <div className="mb-3 flex items-center justify-between">
                  <h4 className="m-0 text-base font-semibold text-gray-800">
                    {group.label}
                    <span className="ml-2 text-xs font-normal text-gray-400">
                      {group.multiple ? '(opcional, podés elegir varias)' : '(opcional)'}
                    </span>
                  </h4>
                  {hasSelection && (
                    <button
                      type="button"
                      onClick={() => setSelections(prev => ({ ...prev, [group.id]: [] }))}
                      className="border-none bg-transparent p-0 text-xs text-gray-400 cursor-pointer hover:text-gray-600"
                    >
                      Saltar
                    </button>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {group.items.map((item) => {
                    const isSelected = groupSelections.includes(item.product_format_id);
                    return (
                      <button
                        key={`${item.product_id}-${item.product_format_id}`}
                        type="button"
                        onClick={() => handleToggleOption(group, item.product_format_id)}
                        className={`flex items-center justify-between rounded-lg border-2 px-4 py-3 text-left transition-all ${
                          isSelected
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 bg-white hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${
                            isSelected ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300'
                          }`}>
                            {isSelected && <i className="fa fa-check text-[10px]" />}
                          </span>
                          {item.product_image_url && (
                            <img
                              src={item.product_image_url}
                              alt=""
                              className="h-9 w-9 flex-shrink-0 rounded-md object-cover"
                            />
                          )}
                          <div>
                            <span className="text-sm font-medium text-gray-800">
                              {item.quantity}x {item.product_title}
                            </span>
                            {item.format_name && (
                              <span className="ml-1 text-xs text-gray-500">({item.format_name})</span>
                            )}
                          </div>
                        </div>
                        <span className={`whitespace-nowrap text-sm font-medium ${
                          isSelected ? 'text-green-600' : 'text-gray-500'
                        }`}>
                          +{formatPrice(item.subtotal)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer with price + confirm button */}
        <div className="shrink-0 border-t border-gray-200 px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              {showFromPrice && (
                <span className="mr-1 text-xs text-gray-400">Desde</span>
              )}
              <span className="text-xl font-bold text-green-600">{formatPrice(dynamicPrice)}</span>
            </div>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:enabled:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <span className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Agregando...
                </>
              ) : (
                <>
                  <i className="fa fa-cart-plus" />
                  Agregar al carrito
                </>
              )}
            </button>
          </div>
        </div>

        {/* Bottom spacing for safe area */}
        <div className="shrink-0 pb-[env(safe-area-inset-bottom)] md:pb-0" />
      </div>

      <style>{`
        @keyframes pack-choice-slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        @media (min-width: 768px) {
          @keyframes pack-choice-slide-up {
            from { opacity: 0; transform: translate(-50%, -48%); }
            to { opacity: 1; transform: translate(-50%, -50%); }
          }
        }
      `}</style>
    </div>,
    document.body,
  );
}
